import type { FC } from 'react';

import {
    Grid, Pagination, Popover, Table, Tag,
} from 'antd';
import { LoadingOutlined, MoreOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import type { SpinProps } from 'antd/lib/spin';

import ColoredTooltip from '@/components/Common/ColoredTooltip';
import { Select } from '@/components/Common/UIKit';
import { useStores } from '@/components/hooks';
import type { FileMenu } from '@/types/types';
import {
    AccessApps, LogRecord, ModifyUserRoleDetails, Operations, UserInviteDetails,
} from '@/stores/AuditsStore/interaces';
import i18n from '@/content';
import { tryCopyToClipboard } from '@/components/utils';

import SimpleCell from './SimpleCell';
import TimeStampCell from './TimeStampCell';
import FileIdCell from './FileIdCell';
import AccessAppCell from './AccessAppCell';
import DeviceIdentityCell from './DeviceIdentityCell';
import AuditBreadcrumbs from './AuditBreadcrumbs';
import { Actions, PolicyTag } from '../../FilesTable/Columns';
import useTableHeight from './useTableHeight';
import { FileActionType } from '../../FilesTable/FileAction/menuItems';
import {
    MAP_OPERATION_TYPE_TO_COLOR, PAGE_SIZES_TO_DROPDOWN,
} from '../constants';
import './index.scss';
const { useBreakpoint } = Grid;
const { Column } = Table;

const FILE_MENU: Partial<FileMenu> = {
    options: ['delete_file', 'set_file_policy'],
};

const mainNameSpace = 'auditLogs';
const nameSpace = `${mainNameSpace}.tableColumns`;

interface AuditsTableProps {
    handleMenuClick: (action: FileActionType, fileId: string) => void | Promise<void>;
}

const AuditsTable: FC<AuditsTableProps> = observer(({ handleMenuClick }) => {
    const {
        auditsStore: {
            isLoading, logsList, logsToDisplay, fileId,
            pagination: { currentPage, pageSize, nextPageToken },
            handlePageChange, handlePageSizeChange,
        },
    } = useStores();

    const { containerRef, height } = useTableHeight(!!logsToDisplay.length);
    const screens = useBreakpoint();

    const totalRecords = logsList.length;
    // +1 using here to show next page into Ant Paginator
    const totalRecordsToPagination = nextPageToken ? totalRecords + 1 : totalRecords;
    const tableLoading: SpinProps = {
        wrapperClassName: 'audits-table-spinner',
        spinning: isLoading,
        indicator: <LoadingOutlined className="audits-spin" spin />,
    };

    const onClickUsername = (owner: string): void => {
        tryCopyToClipboard(owner, i18n.t(`${nameSpace}.user`));
    };

    const copyAuditValue = (value: string | null | undefined, fieldKey: string): void => {
        tryCopyToClipboard(value, i18n.t(`${nameSpace}.${fieldKey}`));
    };

    return (
        <div data-testid="audits_tableWrapper" className="audits-table-wrapper">
            <div className="pagination-wrapper">
                <div className="pagination-wrapper-breadcrumbs">
                    {fileId && <AuditBreadcrumbs fileId={fileId} />}
                </div>
                <div className="pagination-wrapper-panel">
                    <Pagination
                        showSizeChanger={false}
                        current={currentPage}
                        total={totalRecordsToPagination}
                        onChange={handlePageChange}
                        disabled={isLoading}
                        pageSize={pageSize}
                        showLessItems={!screens.md}
                    />
                    <span className="rows-per-page">
                        {i18n.t(`${mainNameSpace}.rowsPerPage`)}
                    </span>
                    <Select<number>
                        options={PAGE_SIZES_TO_DROPDOWN}
                        onChange={handlePageSizeChange}
                        value={pageSize}
                        disabled={isLoading}
                    />
                </div>
            </div>
            <div className="audits-table-inner-wrapper" ref={containerRef}>
                <Table<LogRecord>
                    data-testid="audits_table"
                    rowKey="timestamp"
                    className="audits-table"
                    dataSource={logsToDisplay}
                    loading={tableLoading}
                    pagination={false}
                    scroll={{ x: true, y: height }}
                >
                    <Column<LogRecord>
                        title={i18n.t(`${nameSpace}.timestamp`)}
                        dataIndex="timestamp"
                        key="timestamp"
                        render={(timestamp: string) => (
                            <TimeStampCell timestamp={timestamp} />
                        )}
                    />
                    <Column<LogRecord>
                        title={i18n.t(`${nameSpace}.user`)}
                        dataIndex="userName"
                        key="userName"
                        render={(text, record) => (
                            <SimpleCell
                                text={record.user}
                                onClick={() => onClickUsername(record?.operation === 'upload'
                                    ? record.owner_id
                                    : record.user)}
                            />
                        )}
                    />
                    <Column<LogRecord>
                        title={i18n.t(`${nameSpace}.operation`)}
                        dataIndex="operation"
                        key="operation"
                        render={(operation: Operations, record) => {
                            const tag = (
                                <Tag
                                    color={MAP_OPERATION_TYPE_TO_COLOR[operation] || '#108ee9'}
                                    onClick={() => copyAuditValue(operation, 'operation')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <code>{i18n.t(`${mainNameSpace}.operationTypes.${operation}`)}</code>
                                </Tag>
                            );
                            if (operation !== Operations.bigidscan) return tag;
                            const counts = (record.details as Record<string, unknown>)?.classifier_counts as Record<string, number> | undefined;
                            if (!counts || !Object.keys(counts).length) return tag;
                            return (
                                <Popover
                                    content={(
                                        <table style={{ borderCollapse: 'collapse' }}>
                                            <tbody>
                                                {Object.entries(counts).map(([name, count]) => (
                                                    <tr key={name}>
                                                        <td style={{ padding: '2px 12px 2px 0', whiteSpace: 'nowrap' }}>{name}</td>
                                                        <td style={{ padding: '2px 0', fontWeight: 600, textAlign: 'right' }}>{count}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                >
                                    <span style={{ cursor: 'pointer' }}>{tag}</span>
                                </Popover>
                            );
                        }}
                    />
                    <Column<LogRecord>
                        title={i18n.t(`${nameSpace}.fileName`)}
                        dataIndex="file_name"
                        key="file_name"
                        width="200px"
                        render={(filename: string | undefined, { child_name: childName }) => {
                            const name = childName || filename;
                            return filename && (
                                <ColoredTooltip title={name} placement="topLeft">
                                    <SimpleCell
                                        className="file-name"
                                        text={name}
                                        onClick={() => copyAuditValue(name, 'fileName')}
                                    />
                                </ColoredTooltip>
                            );
                        }}
                    />
                    <Column<LogRecord>
                        title={i18n.t(`${nameSpace}.affectedUsers`)}
                        dataIndex="affected_users"
                        key="affected_users"
                        render={(users: string[] | undefined) => (
                            <>
                                {users && users.map((user) => (
                                    <Tag
                                        key={user}
                                        onClick={() => copyAuditValue(user, 'affectedUsers')}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <code>{user}</code>
                                    </Tag>
                                ))}
                            </>
                        )}
                    />
                    <Column<LogRecord>
                        title={i18n.t(`${nameSpace}.roleChange`)}
                        key="role_change"
                        render={(_, { operation, details }) => {
                            if (operation === Operations.modifyuserrole) {
                                const { old_role: oldRole, new_role: newRole } = (details as ModifyUserRoleDetails) || {};
                                if (!oldRole || !newRole) return null;
                                const roleChangeText = `${oldRole} → ${newRole}`;
                                return (
                                    <SimpleCell
                                        text={roleChangeText}
                                        onClick={() => copyAuditValue(roleChangeText, 'roleChange')}
                                    />
                                );
                            }
                            if (operation === Operations.userinvite) {
                                const { role } = (details as UserInviteDetails) || {};
                                if (!role) return null;
                                return (
                                    <SimpleCell
                                        text={role}
                                        onClick={() => copyAuditValue(role, 'roleChange')}
                                    />
                                );
                            }
                            return null;
                        }}
                    />
                    <Column<LogRecord>
                        title={i18n.t(`${nameSpace}.policyName`)}
                        dataIndex="policy_name"
                        key="policy_name"
                        render={(policyName: string | undefined) => (
                            policyName && (
                                <span
                                    className="copyable-audit-field"
                                    onClick={() => copyAuditValue(policyName, 'policyName')}
                                >
                                    <PolicyTag title={policyName} />
                                </span>
                            )
                        )}
                    />
                    <Column<LogRecord>
                        title={i18n.t(`${nameSpace}.rule`)}
                        dataIndex="rule"
                        key="rule"
                        render={(rule: string | undefined) => (
                            rule && (
                                <span
                                    className="copyable-audit-field"
                                    onClick={() => copyAuditValue(rule, 'rule')}
                                >
                                    <PolicyTag title={rule} />
                                </span>
                            )
                        )}
                    />
                    <Column<LogRecord>
                        title={i18n.t(`${nameSpace}.app`)}
                        dataIndex="app"
                        key="app"
                        render={(accessApp: AccessApps | undefined) => (
                            accessApp && (
                                <AccessAppCell
                                    accessApp={accessApp}
                                    onClick={() => copyAuditValue(accessApp, 'app')}
                                />
                            )
                        )}
                    />
                    <Column<LogRecord>
                        title={i18n.t(`${nameSpace}.userIp`)}
                        dataIndex="user_ip"
                        key="user_ip"
                        render={(userIP: string | undefined, { user_agent: userAgentStr, user_country: user_country_code }) => (
                            <DeviceIdentityCell
                                userIP={userIP}
                                userAgentStr={userAgentStr}
                                userCountryCode={user_country_code}
                                onCopyUserIP={() => copyAuditValue(userIP, 'userIp')}
                                onCopyUserCountry={() => copyAuditValue(user_country_code, 'userCountry')}
                                onCopyUserAgent={() => copyAuditValue(userAgentStr, 'userAgent')}
                            />
                        )}
                    />
                    <Column<LogRecord>
                        title={i18n.t(`${nameSpace}.fileId`)}
                        dataIndex="file_id"
                        key="file_id"
                        render={(fileId: string | undefined, { origin_id: originId, child_id: childId }) => {
                            const id = childId || fileId;
                            return (id || originId) && <FileIdCell fileId={id} originId={originId} />;
                        }}
                    />
                    <Column<LogRecord>
                        title=""
                        dataIndex="file_id"
                        key="file_id"
                        render={(fileId: string | undefined, { operation, child_id: childId, details }) => {
                            const isGroupAction = details && typeof details === 'object' && 'group_id' in details;
                            const id = childId || fileId;
                            return (
                                <>
                                    {!isGroupAction && id && operation !== 'delete' && (
                                        <Actions
                                            fileId={id}
                                            openIcon={<MoreOutlined />}
                                            menu={FILE_MENU}
                                            handleMenuClick={handleMenuClick}
                                        />
                                    )}
                                </>
                            );
                        }}
                    />
                </Table>
            </div>
        </div>
    );
});

export default AuditsTable;
